'use client';

import type { ReactElement } from 'react';
import { cn } from '../../lib/cn';
import { type LinkLike, NativeLink } from '../../lib/link';

/**
 * Wordmark SLIMS PRO. L'asset di default è scuro di suo: sul tema chiaro va
 * mostrato tale e quale (nel mock `[data-theme="light"] #logo-icon{filter:none}`),
 * sul tema scuro va schiarito. Un client può passare il proprio logo (`src`) e
 * scegliere se tingerlo come il wordmark (`variant="mask"`) o mostrarlo con i suoi
 * colori (`variant="image"`).
 *
 * Gli asset vivono in `public/` dell'app; i percorsi restano qui perché "quale
 * file per quale tema" è una decisione di presentazione.
 */

export type BrandTheme = 'light' | 'dark';

/**
 * Taglia del wordmark: `header` (28px) è quella del chrome, allineata ai controlli
 * dell'header; `auth` (40px) è quella delle pagine di login, dove il logo è
 * l'elemento principale della schermata.
 */
export type BrandLogoSize = 'header' | 'auth';

export interface BrandLogoProps {
	/** Tema corrente dell'app: seleziona la variante del wordmark. */
	theme: BrandTheme;
	/** Taglia del wordmark. Default: `header`. */
	size?: BrandLogoSize;
	/** Se presente il logo diventa un link (via `linkComponent`). */
	href?: string;
	/** Componente link da usare (default: link nativo `<a>`). */
	linkComponent?: LinkLike;
	/** Sorgente del logo (servita da `public/`). Default: `/logo-pro.png`. */
	src?: string;
	/** Testo alternativo del logo. Default: `SLIMS PRO`. */
	alt?: string;
	/**
	 * `mask` (default): tinto come il wordmark sul tema scuro, immagine sul chiaro.
	 * `image`: l'immagine su entrambi i temi, per loghi che devono tenere i propri colori.
	 */
	variant?: 'mask' | 'image';
}

const LOGO_HEIGHT: Record<BrandLogoSize, string> = {
	header: 'h-7',
	auth: 'h-10',
};

/** Il resto del riquadro: la larghezza segue le proporzioni dell'asset. */
const LOGO_CLASS = 'w-auto flex-none';

/** Avvolge il wordmark in un link quando `href` è presente. */
function withLink(
	logo: ReactElement,
	href: string | undefined,
	Link: LinkLike = NativeLink,
): ReactElement {
	if (!href) return logo;
	return (
		<Link href={href} className="flex-none transition-opacity hover:opacity-80">
			{logo}
		</Link>
	);
}

/**
 * Riquadro del wordmark PRO sul tema scuro: `logo-pro.png` è 336×88, quindi a 28px
 * di altezza la larghezza è 107px e a 40px è 153px. Vanno fissate esplicitamente
 * perché l'elemento è uno `<span>` mascherato, senza dimensioni intrinseche da cui
 * dedurle come farebbe un'immagine vera.
 */
const PRO_MASK_BOX: Record<BrandLogoSize, string> = {
	header: 'h-7 w-[107px]',
	auth: 'h-10 w-[153px]',
};

const PRO_MASK_CLASS = 'flex-none block bg-brand';

const PRO_MASK = [
	'[mask-position:center]',
	'[mask-repeat:no-repeat]',
	'[mask-size:contain]',
	'[-webkit-mask-position:center]',
	'[-webkit-mask-repeat:no-repeat]',
	'[-webkit-mask-size:contain]',
];

/**
 * La sorgente della maschera va inline e non in una classe: `logo-pro.png` è un
 * asset servito da `public/`, e un `url()` dentro al CSS generato il bundler
 * proverebbe a risolverlo a build time (`Could not resolve: "/logo-pro.png"`).
 */
function maskStyle(src: string) {
	return { maskImage: `url(${src})`, WebkitMaskImage: `url(${src})` };
}

/**
 * Wordmark SLIMS PRO, tinto dell'ambra dell'interfaccia sul tema scuro.
 *
 * L'asset fa da maschera e viene riempito con `bg-brand`: è lo stesso token della
 * voce di sidebar attiva, quindi logo e navigazione hanno per costruzione la
 * stessa tinta e la seguono se il token cambia. Un `filter` non potrebbe
 * garantirlo — arriverebbe solo *vicino* al colore voluto.
 *
 * Sul tema chiaro resta l'immagine originale: il suo marrone-arancio è già la
 * controparte chiara della stessa tinta.
 */
export function ProLogo({
	theme,
	size = 'header',
	href,
	linkComponent,
	src = '/logo-pro.png',
	alt = 'SLIMS PRO',
	variant = 'mask',
}: BrandLogoProps) {
	return withLink(
		theme === 'dark' && variant === 'mask' ? (
			<span
				role="img"
				aria-label={alt}
				style={maskStyle(src)}
				className={cn(PRO_MASK_BOX[size], PRO_MASK_CLASS, PRO_MASK)}
			/>
		) : (
			<img src={src} alt={alt} className={cn(LOGO_HEIGHT[size], LOGO_CLASS)} />
		),
		href,
		linkComponent,
	);
}
